import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Alert, Badge, Button, Form } from 'react-bootstrap';
import { useSearchParams, Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { reportService, lockedSignupDomainService, signupDomainService } from '../services/apiService';
import StackedTable from '../components/StackedTable';
import ReportHeader from '../components/ReportHeader';
import { LoadingState, ErrorState, serverMessage } from '../components/PageState';
import { useConfirm } from '../components/ConfirmDialog';
import { usePermissions } from '../context/PermissionsContext';
import { useAuthToken } from '../hooks/useAuthToken';
import { useMediaQuery, BELOW_DESKTOP_QUERY } from '../hooks/useMediaQuery';
import { EMPTY, formatDate, formatNumber, statusLabel } from '../utils/format';
import { planLabel } from '../components/PlanFilter';
import { rowClick } from '../utils/rows';
import { downloadCsv } from '../utils/csv';

/**
 * Email domains whose users sit in more than one company. Each domain expands into its companies,
 * with buttons to open the company or, for admins, to lock / link the domain for it (staff-added
 * rows go straight to APPROVED). The likely-duplicate flag is the outreach list.
 */
const SharedEmailDomains = () => {
  const refreshToken = useAuthToken();
  const { canEdit } = usePermissions();
  const { confirm, confirmDialog } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [includePublic, setIncludePublic] = useState(false);
  const [minCompanies, setMinCompanies] = useState(2);
  const [search, setSearch] = useState(searchParams.get('domain') || '');
  const [onlyDuplicates, setOnlyDuplicates] = useState(false);
  const [sortBy, setSortBy] = useState('companies');
  const [expanded, setExpanded] = useState(() => new Set(searchParams.get('domain') ? [searchParams.get('domain')] : []));
  const [busyKey, setBusyKey] = useState(null);
  // Below desktop the per-company buttons wrap, so an expanded domain does not push the table wider than the screen
  const belowDesktop = useMediaQuery(BELOW_DESKTOP_QUERY);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includePublic, minCompanies]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      await refreshToken();
      const response = await reportService.sharedEmailDomains({ includePublic, minCompanies });
      setReport(response.data);
    } catch (err) {
      setLoadError('Could not load the shared email domains.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const rows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    let list = report.domains.filter((d) => !q || d.domain.includes(q));
    if (onlyDuplicates) list = list.filter((d) => d.likelyDuplicate);
    const sorters = {
      companies: (a, b) => b.companyCount - a.companyCount || b.totalUsers - a.totalUsers,
      users: (a, b) => b.totalUsers - a.totalUsers,
      domain: (a, b) => a.domain.localeCompare(b.domain),
    };
    return [...list].sort(sorters[sortBy]);
  }, [report, search, onlyDuplicates, sortBy]);

  const toggle = (domain) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain); else next.add(domain);
      return next;
    });
  };

  const onSearchChange = (value) => {
    setSearch(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value.trim()) next.set('domain', value.trim()); else next.delete('domain');
      return next;
    });
  };

  const addDomainFor = async (kind, company, domain) => {
    const lock = kind === 'locked';
    const name = company.companyName || company.companyId;
    const confirmed = await confirm({
      title: lock ? `Lock ${domain} for ${name}?` : `Link ${domain} for ${name}?`,
      body: lock
        ? `Nobody with an @${domain} email will be able to create a new Team Today account. The lock is listed under ${name} and is approved straight away.`
        : `Anyone who signs up with an @${domain} email will join ${name} automatically, while the company's sign-up domain switch is on. It is approved straight away.`,
      confirmLabel: lock ? 'Lock domain' : 'Link domain',
    });
    if (!confirmed) return;
    const key = `${kind}-${company.companyId}-${domain}`;
    try {
      setBusyKey(key);
      setError(null);
      await refreshToken();
      const service = lock ? lockedSignupDomainService : signupDomainService;
      await service.add(company.companyId, domain);
      await fetchReport();
    } catch (err) {
      setError(serverMessage(err, `Could not ${lock ? 'lock' : 'link'} ${domain} for ${name}.`));
      console.error(err);
    } finally {
      setBusyKey(null);
    }
  };

  const exportCsv = () => downloadCsv(
    'shared-email-domains',
    ['domain', 'companies', 'total_users', 'largest_company_share', 'likely_duplicate', 'company_id', 'company_name', 'account_type',
      'users_on_domain', 'paid_users', 'free_users', 'total_users', 'last_user_at', 'signup_domain_status', 'locked_domain_status'],
    rows.flatMap((d) => d.companies.map((c) => [
      d.domain, d.companyCount, d.totalUsers, `${Math.round(d.dominantShare * 100)}%`, d.likelyDuplicate ? 'yes' : 'no',
      c.companyId, c.companyName || '', c.accountType || '', c.users, c.paidUsers ?? 0, c.freeUsers ?? 0, c.totalUsers ?? 0,
      c.lastUserAt ? new Date(c.lastUserAt).toISOString().slice(0, 10) : '', c.signupDomainStatus || '', c.lockedDomainStatus || '',
    ])),
  );

  const statusBadge = (status) => status && (
    <Badge bg={status === 'APPROVED' ? 'success' : status === 'REJECTED' ? 'danger' : 'warning'} className="ms-1">
      {statusLabel(status)}
    </Badge>
  );

  const duplicateBadge = <Badge bg="warning" className="ms-2">Likely duplicate</Badge>;

  const summary = report?.summary;
  const tileValue = (value) => (summary ? formatNumber(value) : EMPTY);

  return (
    <Container fluid className="py-4">
      {confirmDialog}
      <ReportHeader
        title="Shared email domains"
        description="Email domains whose users are spread across more than one company. Usually a duplicate account, sometimes an agency, occasionally a mis-set sign-up domain."
        actions={(
          <Button variant="outline-secondary" onClick={exportCsv} disabled={!rows.length}>
            <Download size={14} className="me-1" aria-hidden="true" />Export CSV
          </Button>
        )}
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {!loadError && (
        <Row className="g-3 g-md-4 mb-4">
          <Col xs={6} md={3}>
            {/* The outreach list leads, and its tile doubles as the filter for it */}
            <Card
              className="shadow-sm h-100 stat-tile"
              style={onlyDuplicates ? { borderColor: 'var(--color-accent-emphasis)' } : undefined}
            >
              <Card.Body className="text-center">
                <h6 className="text-muted mb-2">Likely duplicates</h6>
                <h2 className="text-primary mb-0">{tileValue(summary?.likelyDuplicates)}</h2>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => setOnlyDuplicates(!onlyDuplicates)}
                  aria-pressed={onlyDuplicates}
                  disabled={!summary}
                >
                  {onlyDuplicates ? 'Show all domains' : 'Show only these'}
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="shadow-sm h-100 stat-tile"><Card.Body className="text-center">
              <h6 className="text-muted mb-2">Shared domains</h6>
              <h2 className="mb-0">{tileValue(summary?.sharedDomains)}</h2>
            </Card.Body></Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="shadow-sm h-100 stat-tile"><Card.Body className="text-center">
              <h6 className="text-muted mb-2">Companies involved</h6>
              <h2 className="mb-0">{tileValue(summary?.companiesInvolved)}</h2>
            </Card.Body></Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="shadow-sm h-100 stat-tile"><Card.Body className="text-center">
              <h6 className="text-muted mb-2">Users affected</h6>
              <h2 className="mb-0">{tileValue(summary?.usersAffected)}</h2>
            </Card.Body></Card>
          </Col>
        </Row>
      )}

      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <Row className="g-2 align-items-center">
            <Col md={3}>
              <Form.Control size="sm" type="search" placeholder="Search domain" aria-label="Search domain" value={search} onChange={(e) => onSearchChange(e.target.value)} />
            </Col>
            <Col xs={6} md="auto">
              <Form.Select size="sm" value={minCompanies} onChange={(e) => setMinCompanies(Number(e.target.value))} aria-label="Shared by at least">
                <option value={2}>2+ companies</option>
                <option value={3}>3+ companies</option>
                <option value={5}>5+ companies</option>
              </Form.Select>
            </Col>
            <Col xs={6} md="auto">
              <Form.Select size="sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort by">
                <option value="companies">Most companies</option>
                <option value="users">Most users</option>
                <option value="domain">Domain A to Z</option>
              </Form.Select>
            </Col>
            <Col md="auto">
              <Form.Check type="switch" id="onlyDuplicates" label="Likely duplicates only" checked={onlyDuplicates} onChange={(e) => setOnlyDuplicates(e.target.checked)} />
            </Col>
            <Col md="auto">
              <Form.Check type="switch" id="includePublic" label="Include public providers" checked={includePublic} onChange={(e) => setIncludePublic(e.target.checked)} />
            </Col>
            <Col className="text-md-end text-muted small">{formatNumber(rows.length)} of {formatNumber(report?.domains.length ?? 0)} domains</Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {loadError ? (
            <ErrorState inCard message={loadError} onRetry={fetchReport} />
          ) : loading ? (
            <LoadingState label="Loading domains" />
          ) : rows.length === 0 ? (
            <div className="p-4 text-center text-muted">No domain is shared across companies with these filters</div>
          ) : (
            /* On phones each domain is a card titled by the domain (shown in the chevron cell). Below
               desktop the rules summary is left to the expanded detail, which lists each company's rules */
            <StackedTable responsive hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th className="d-none d-md-table-cell">Domain</th>
                  <th className="text-end">Companies</th>
                  <th className="text-end">Users</th>
                  <th className="text-end" title="The share of the domain's users in the company that has the most of them">Largest company's share</th>
                  <th className="d-none d-xl-table-cell">Domain rules</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const open = expanded.has(d.domain);
                  const linked = d.companies.filter((c) => c.signupDomainStatus);
                  const locked = d.companies.filter((c) => c.lockedDomainStatus);
                  return (
                    <React.Fragment key={d.domain}>
                      <tr onClick={rowClick(() => toggle(d.domain))} style={{ cursor: 'pointer' }}>
                        <td className="text-muted">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-muted align-baseline"
                            onClick={() => toggle(d.domain)}
                            aria-expanded={open}
                            aria-label={`${open ? 'Hide' : 'Show'} the companies on ${d.domain}`}
                          >
                            {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
                          </Button>
                          <span className="d-md-none ms-1">
                            <code>{d.domain}</code>
                            {d.likelyDuplicate && duplicateBadge}
                            {d.publicProvider && <Badge bg="secondary" className="ms-2">Public provider</Badge>}
                          </span>
                        </td>
                        <td className="d-none d-md-table-cell">
                          <code>{d.domain}</code>
                          {d.likelyDuplicate && duplicateBadge}
                          {d.publicProvider && <Badge bg="secondary" className="ms-2">Public provider</Badge>}
                        </td>
                        <td className="text-end">{formatNumber(d.companyCount)}</td>
                        <td className="text-end">{formatNumber(d.totalUsers)}</td>
                        <td className="text-end">{Math.round(d.dominantShare * 100)}%</td>
                        <td className="d-none d-xl-table-cell">
                          {linked.map((c) => <span key={`l-${c.companyId}`} className="me-2 small">Linked by {c.companyName}{statusBadge(c.signupDomainStatus)}</span>)}
                          {locked.map((c) => <span key={`k-${c.companyId}`} className="me-2 small">Locked by {c.companyName}{statusBadge(c.lockedDomainStatus)}</span>)}
                          {!linked.length && !locked.length && <span className="text-muted small">None</span>}
                        </td>
                      </tr>
                      {open && (
                        <tr className="table-light">
                          <td className="d-none d-md-table-cell"></td>
                          <td colSpan={5} className="p-0">
                            <StackedTable size="sm" className="mb-0">
                              <thead>
                                <tr className="text-muted small">
                                  <th>Company</th>
                                  <th>Plan</th>
                                  <th className="text-end">Users on domain</th>
                                  <th className="text-end d-none d-xl-table-cell">Share</th>
                                  <th className="text-end d-none d-md-table-cell">Paid users</th>
                                  <th className="text-end d-none d-md-table-cell">Total users</th>
                                  <th className="d-none d-xl-table-cell">Newest user</th>
                                  <th>Rules</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {d.companies.map((c) => {
                                  const busy = busyKey && busyKey.endsWith(`${c.companyId}-${d.domain}`);
                                  return (
                                    <tr key={c.companyId}>
                                      <td><Link className="cell-link" to={`/companies/${c.companyId}`}>{c.companyName || c.companyId}</Link></td>
                                      <td>{planLabel(c.accountType)}</td>
                                      <td className="text-end">{formatNumber(c.users)}</td>
                                      <td className="text-end d-none d-xl-table-cell">{Math.round((c.users / d.totalUsers) * 100)}%</td>
                                      <td className="text-end d-none d-md-table-cell">{formatNumber(c.paidUsers ?? 0)}</td>
                                      <td className="text-end d-none d-md-table-cell" title={`${c.paidUsers ?? 0} paid + ${c.freeUsers ?? 0} free`}>{formatNumber(c.totalUsers ?? 0)}</td>
                                      <td className="d-none d-xl-table-cell">{formatDate(c.lastUserAt)}</td>
                                      <td>
                                        {c.signupDomainStatus && <span className="small">Linked{statusBadge(c.signupDomainStatus)}</span>}
                                        {c.lockedDomainStatus && <span className="small ms-2">Locked{statusBadge(c.lockedDomainStatus)}</span>}
                                        {!c.signupDomainStatus && !c.lockedDomainStatus && <span className="text-muted small">{EMPTY}</span>}
                                      </td>
                                      <td className={belowDesktop ? 'text-end' : 'text-end text-nowrap'}>
                                        {/* Locking and linking change who can sign up, so view-only staff see only the company link */}
                                        {canEdit && !c.lockedDomainStatus && !d.publicProvider && (
                                          <Button variant="outline-secondary" size="sm" className="me-2" disabled={!!busy}
                                                  onClick={() => addDomainFor('locked', c, d.domain)}>
                                            Lock for this company
                                          </Button>
                                        )}
                                        {canEdit && !c.signupDomainStatus && !d.publicProvider && (
                                          <Button variant="outline-secondary" size="sm" className="me-2" disabled={!!busy}
                                                  onClick={() => addDomainFor('signup', c, d.domain)}>
                                            Link for this company
                                          </Button>
                                        )}
                                        <Button as={Link} to={`/companies/${c.companyId}?tab=config`} variant="outline-primary" size="sm">
                                          Domain settings
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </StackedTable>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </StackedTable>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SharedEmailDomains;
